import { useQuery } from "@apollo/client";
import { View } from "react-native";
import { Text, ActivityIndicator } from "react-native-paper";
import { useState } from "react";
import { ScreenProps, SubmissionStatus } from "../types";

import { getStatusType } from "../../components/ChipPoint";
import { Button, ScrollableContainer } from "../../components";
import { readableDate } from "../../utils/dateUtils";
import PopOver from "../../components/PopOver";
import { Query, QUERY } from "./types";
import ActionsContainer from "../../components/ActionsContainer";
import ScreenContainer from "../../components/ScreenContainer";
import UpdateReviewer from "../../components/UpdateReviewer";
import MyActivityCancelRequest from "./MyActivityCancelRequestModal";
import { isCancelledOrExpired } from "./utils";

const Divider = () => <View className="border-t-2 border-sky-lighter" />;

export default function MyActivityManageRequestScreen({ navigation, route }: ScreenProps<"MyActivityManageRequest">) {
  const {
    params: { id },
  } = route;

  const { data } = useQuery<Query>(QUERY, {
    variables: {
      id,
    },
  });

  const [showUpdateReviewer, setShowUpdateReviewer] = useState(false);
  const [showCancelInterrupt, setShowCancelInterrupt] = useState(false);
  const dismissCancelInterrupt = () => setShowCancelInterrupt(false);
  const [cancelRequestModalVisible, setCancelRequestModalVisible] = useState(false);

  const submission = data?.currentMember.deferredCompSubmission;
  if (!submission) return <ActivityIndicator className="flex-1 justify-center items-center bg-sky-white" />;
  const { status, expiresSoon, expirationDate, reviewRequestDate, rejectionMessage, rejectionReason } = submission;

  const { statusLabel } = getStatusType(status, expiresSoon);
  const isRejected = status === SubmissionStatus.REJECTED;

  const onPressCancelRequest = () => {
    dismissCancelInterrupt();
    setCancelRequestModalVisible(true);
  };
  const onSuccess = () => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate("DeferredComp"));

  return (
    <ScreenContainer testID="MyActivityManageRequestScreen" navigationTitle="Manage Request">
      <MyActivityCancelRequest
        {...{
          id,
          visible: cancelRequestModalVisible,
          onSuccess,
        }}
        onDismissModal={() => setCancelRequestModalVisible(false)}
      />

      <UpdateReviewer
        pointsRequest={submission}
        visible={showUpdateReviewer}
        onSuccess={onSuccess}
        onDismissModal={() => setShowUpdateReviewer(false)}
      />
      <PopOver visible={showCancelInterrupt} onDismiss={dismissCancelInterrupt}>
        <Text className="font-700 text-regular-tight">Are you sure you wish to cancel this request?</Text>
        <Text className="mt-2 font-400 text-regular-normal">
          Activity requests cannot be sent to another reviewer once canceled. A new request can be created if you wish to claim
          points for this activity.
        </Text>

        <View className="flex-row mt-8">
          <View className="flex-1">
            <Button size="small" onPress={dismissCancelInterrupt}>
              Go Back
            </Button>
          </View>
          <Button mode="secondary" size="small" onPress={onPressCancelRequest} testID="pop-over-cancel-button">
            Cancel Request
          </Button>
        </View>
      </PopOver>

      <ScrollableContainer>
        <View className="m-6">
          <Text className="text-small-tight font-700 text-ink-darkest">Status</Text>
          {expiresSoon || isRejected ? (
            <Text className="text-regular-tight font-700 text-red-dark mt-2">{statusLabel}</Text>
          ) : (
            <Text className="text-regular-tight font-700 text-ink-darker mt-2">{statusLabel}</Text>
          )}
        </View>
        {isRejected && rejectionReason && (
          <>
            <Divider />
            <View className="m-6">
              <Text className="text-small-tight font-700 text-ink-darkest">Reason reviewer is unable to confirm</Text>
              <Text className="text-regular-tight font-400 text-ink-dark mt-2">{rejectionReason}</Text>
              {rejectionMessage && (
                <>
                  <Text className="text-small-tight font-700 text-ink-darkest mt-4">Message from Reviewer</Text>
                  <Text className="text-regular-tight font-400 text-ink-dark mt-2">{rejectionMessage}</Text>
                </>
              )}
            </View>
          </>
        )}
        <Divider />
        <View className="m-6 pb-6">
          <Text className="text-small-tight font-700 text-ink-darkest">Sent</Text>
          <Text className="text-regular-tight font-400 text-ink mt-2">{readableDate(reviewRequestDate)}</Text>

          <Text className="text-small-tight font-700 text-ink-darkest mt-4">Expires</Text>
          {expiresSoon ? (
            <Text className={`text-regular-tight font-400 text-red-dark mt-2`}>{readableDate(expirationDate)}</Text>
          ) : (
            <Text className={`text-regular-tight font-400 text-ink-dark mt-2`}>{readableDate(expirationDate)}</Text>
          )}
        </View>
      </ScrollableContainer>

      {!isCancelledOrExpired(status) && (
        <ActionsContainer>
          <View className="sm:flex-1">
            <Button
              mode="contained"
              onPress={() => {
                setShowUpdateReviewer(true);
              }}
            >
              Send to Another Reviewer
            </Button>
          </View>

          <View className="mt-4 sm:mt-0 sm:ml-6 sm:flex-1">
            <Button
              mode="outlined"
              onPress={() => {
                setShowCancelInterrupt(true);
              }}
            >
              Cancel Request
            </Button>
          </View>
        </ActionsContainer>
      )}
    </ScreenContainer>
  );
}
